import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { HEADERS_EXPORT_BEN, POST_LOAN_DB2 } from "../../API/apiUrls";

// กลุ่มงานตามสถานะ (ลำดับเดียวกับกราฟ)
export const STATUS_GROUPS = [
  { key: "waitLawsuit", label: "รอฟ้อง", color: "FFFF4D4F" },
  { key: "withBackCase", label: "มีวันนัดศาล", color: "FFFA8C16" },
  { key: "withRedCase", label: "ศาลตัดสิน/พิพากษา", color: "FF1677FF" },
  { key: "ageeement", label: "ยอมความ", color: "FF13C2C2" },
  { key: "finish", label: "จบคดี", color: "FF52C41A" },
  { key: "withdrawCase", label: "ถอนฟ้อง", color: "FF8C8C8C" },
];

// const DEBT_NCSHPRC_TYPES = ["lsfhp-h-ksm", "lsfhp-ksm", "rpsl", "rpsl-h"];
const DEBT_NCSHPRC_TYPES = ["rpsl", "rpsl-h"];
const latestOf = (arr) => [...(arr || [])].sort((a, b) => b.id - a.id)[0];

// ทำยอม: สถานะล่าสุด = 5 หรือผลคำพิพากษาเป็นทำยอม
export const isSettlement = (item) =>
  item.MAIN_STATUS_ID === 5 ||
  !!latestOf(item.work_judges)?.mark?.includes("ทำยอม");

export const getDebt = (db2) => {
  if (!db2) return null;
  return DEBT_NCSHPRC_TYPES.includes(db2.LOAN?.DATA_TYPE)
    ? db2.LOAN?.NCSHPRC
    : db2.LOAN?.TOTPRC;
};

// ยอดหนี้คงเหลือ = ยอดหนี้ - จ่ายมาทั้งหมด (SMPAY)
export const getRemainDebt = (db2) => {
  const debt = getDebt(db2);
  if (debt === null || debt === undefined || debt === "") return null;
  const remain = Number(debt) - Number(db2.LOAN?.SMPAY || 0);
  return isNaN(remain) ? null : Math.round(remain * 100) / 100;
};

export const getCustomerName = (item) =>
  `${item.CUSTOMER_TNAME || ""}${item.CUSTOMER_FNAME || ""} ${
    item.CUSTOMER_LNAME || ""
  }`.trim();

// เพิ่มข้อมูลที่ตาราง / excel ใช้ร่วมกัน
export const enrichCase = (item, loanByContno = {}) => {
  const expenses = item.work_expense_logs || [];
  return {
    ...item,
    db2: loanByContno[item.CONTNO?.trim()],
    latestJudge: latestOf(item.work_judges),
    latestMemo: latestOf(item.working_logs)?.MEMO || null,
    expenseWithdraw: expenses.reduce(
      (sum, e) => sum + Number(e.withdraw || 0),
      0,
    ),
    expensePay: expenses.reduce((sum, e) => sum + Number(e.pay || 0), 0),
    defendants: [...(item.work_judge_defendants || [])].sort(
      (a, b) =>
        (a.judge_number || 0) - (b.judge_number || 0) ||
        (a.defendant_number || 0) - (b.defendant_number || 0),
    ),
    settlement: isSettlement(item),
  };
};

// โหลดข้อมูลสัญญาจาก POST_LOAN_DB2 -> { CONTNO: item }
export const loadLoanByContno = async (items) => {
  const contnos = [...new Set(items.map((i) => i.CONTNO).filter(Boolean))];
  if (contnos.length === 0) return {};
  const response = await axios.post(
    POST_LOAN_DB2,
    { CONTNO: contnos.map((val) => `'${val}'`).join(",") },
    { headers: HEADERS_EXPORT_BEN },
  );
  const mapped = {};
  (Array.isArray(response.data) ? response.data : []).forEach((item) => {
    const contno = item?.LOAN?.CONTNO;
    if (contno) mapped[contno.trim()] = item;
  });
  return mapped;
};

// ---------- Excel ----------

const toNumber = (value) =>
  value === null || value === undefined || value === "" || isNaN(Number(value))
    ? null
    : Number(value);

// ค่าระดับจำเลย: ทำยอม = "-", จำเลยคนเดียว = ตัวเลข, หลายคน = ข้อความแยกบรรทัด
const defendantValue = (record, field, isMoney) => {
  if (record.settlement || record.defendants.length === 0) return "-";
  if (record.defendants.length === 1) {
    return toNumber(record.defendants[0][field]) ?? "-";
  }
  return record.defendants
    .map((d) => {
      const n = toNumber(d[field]);
      const text =
        n === null ? "-" : isMoney ? n.toLocaleString("en-US") : String(n);
      return `จ.${d.defendant_number ?? "-"} ${text}`;
    })
    .join("\n");
};

const MONEY_FMT = "#,##0.00";

// คอลัมน์ excel ให้ตรงกับตารางใน DataCheck
// showLawyer: แสดงคอลัมน์ ผู้รับผิดชอบ (ใช้ตอนเลือกทนายทั้งหมด)
const buildColumns = (formatDate, showLawyer) => {
  const date = (v) => (v ? formatDate(v) : "-");
  const columns = [
    { header: "ลำดับ", width: 7, value: (r, i) => i + 1 },
    { header: "เลขสัญญา", width: 14, value: (r) => r.CONTNO || "-" },
    {
      header: "ชื่อลูกค้า",
      width: 30,
      value: (r) => getCustomerName(r) || "-",
    },
    {
      key: "LAWYER_NNAME",
      header: "ผู้รับผิดชอบ",
      width: 16,
      value: (r) => r.LAWYER_NNAME || "-",
    },
    { header: "สาขา", width: 8, value: (r) => r.db2?.LOAN?.LOCAT || "-" },
    {
      header: "สถานะล่าสุด",
      width: 20,
      value: (r) => r.main_status_description || "-",
    },
    {
      group: "ฟ้อง",
      header: "ศาล",
      width: 22,
      value: (r) => r.provincial_court || "-",
    },
    {
      group: "ฟ้อง",
      header: "คดีดำ",
      width: 16,
      value: (r) => r.black_case_number || "-",
    },
    {
      group: "ฟ้อง",
      header: "วันฟ้อง",
      width: 13,
      value: (r) => date(r.date_of_plaint),
    },
    {
      group: "ฟ้อง",
      header: "ทุนทรัพย์ฟ้อง",
      width: 15,
      money: true,
      value: (r) => toNumber(r.litigation_funds),
    },
    {
      group: "ฟ้อง",
      header: "ค่าใช้จ่าย (เบิก)",
      width: 15,
      money: true,
      value: (r) => (r.work_expense_logs?.length ? r.expenseWithdraw : null),
    },
    {
      group: "ฟ้อง",
      header: "ค่าใช้จ่าย (จ่ายจริง)",
      width: 15,
      money: true,
      value: (r) => (r.work_expense_logs?.length ? r.expensePay : null),
    },
    {
      group: "ชั้นศาล",
      header: "คดีแดง",
      width: 16,
      value: (r) => r.latestJudge?.red_case_number || "-",
    },
    {
      group: "ชั้นศาล",
      header: "วันพิพากษา",
      width: 13,
      value: (r) => date(r.latestJudge?.judge_date),
    },
    {
      group: "ชั้นศาล",
      // sheet ยอมความ ใช้คำว่า ทำยอม
      header: (sheet) => (sheet.name === "ยอมความ" ? "ยอดทำยอม" : "ยอดพิพากษา"),
      width: 15,
      money: true,
      value: (r) => toNumber(r.latestJudge?.judgement),
    },
    {
      group: "ชั้นศาล",
      header: "ค่าขาดประโยชน์",
      width: 15,
      money: true,
      value: (r) => defendantValue(r, "cost_of_uselessness", true),
    },
    {
      group: "ชั้นศาล",
      header: "ค่าขาดประโยชน์รายเดือน",
      width: 18,
      money: true,
      value: (r) => defendantValue(r, "cost_of_useleseness_per_month", true),
    },
    {
      group: "ชั้นศาล",
      header: "จำนวนเดือน",
      width: 11,
      value: (r) => defendantValue(r, "cost_of_useleseness_month", false),
    },
    {
      group: "ข้อมูลสัญญา",
      header: "ยอดหนี้",
      width: 15,
      money: true,
      value: (r) => toNumber(getDebt(r.db2)),
    },
    {
      group: "ข้อมูลสัญญา",
      header: "จ่ายมาทั้งหมด",
      width: 15,
      money: true,
      value: (r) => (r.db2 ? toNumber(r.db2.LOAN?.SMPAY) : null),
    },
    {
      group: "ข้อมูลสัญญา",
      header: "ยอดหนี้คงเหลือ",
      width: 15,
      money: true,
      value: (r) => getRemainDebt(r.db2),
    },
    {
      group: "ข้อมูลสัญญา",
      header: "จ่ายล่าสุด",
      width: 13,
      value: (r) => date(r.db2?.LOAN?.LPAYD),
    },
    {
      header: "หมายเหตุ",
      width: 35,
      wrap: true,
      value: (r) => r.latestMemo || "-",
    },
  ];
  return showLawyer ? columns : columns.filter((c) => c.key !== "LAWYER_NNAME");
};

const thinBorder = {
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
};

const fill = (argb) => ({
  type: "pattern",
  pattern: "solid",
  fgColor: { argb },
});

// ชื่อ sheet ห้ามมี \ / ? * [ ] : และยาวไม่เกิน 31 ตัว
const safeSheetName = (name) => name.replace(/[\\/?*[\]:]/g, "-").slice(0, 31);

const addCaseSheet = (workbook, sheet, columns) => {
  const { name, color, rows, subtitle } = sheet;
  const headerOf = (col) =>
    typeof col.header === "function" ? col.header(sheet) : col.header;
  const ws = workbook.addWorksheet(safeSheetName(name), {
    views: [{ state: "frozen", xSplit: 2, ySplit: 4 }],
  });
  const lastCol = columns.length;
  ws.columns = columns.map((c) => ({ width: c.width }));

  // หัวรายงาน
  ws.mergeCells(1, 1, 1, lastCol);
  const title = ws.getCell(1, 1);
  title.value = `${name} (${rows.length} สัญญา)`;
  title.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
  title.fill = fill(color);
  title.alignment = { vertical: "middle", horizontal: "left" };
  ws.getRow(1).height = 24;

  ws.mergeCells(2, 1, 2, lastCol);
  ws.getCell(2, 1).value = subtitle || "";
  ws.getCell(2, 1).font = { italic: true, color: { argb: "FF595959" } };

  // หัวตาราง 2 แถว (กลุ่ม / คอลัมน์) เหมือนตารางในหน้าจอ
  const groupRow = 3;
  const headRow = 4;
  let i = 0;
  while (i < columns.length) {
    const col = columns[i];
    if (!col.group) {
      ws.mergeCells(groupRow, i + 1, headRow, i + 1);
      ws.getCell(groupRow, i + 1).value = headerOf(col);
      i += 1;
      continue;
    }
    let j = i;
    while (j + 1 < columns.length && columns[j + 1].group === col.group) j += 1;
    ws.mergeCells(groupRow, i + 1, groupRow, j + 1);
    ws.getCell(groupRow, i + 1).value = col.group;
    for (let k = i; k <= j; k += 1) {
      ws.getCell(headRow, k + 1).value = headerOf(columns[k]);
    }
    i = j + 1;
  }
  [groupRow, headRow].forEach((r) => {
    for (let c = 1; c <= lastCol; c += 1) {
      const cell = ws.getCell(r, c);
      cell.font = { bold: true };
      cell.fill = fill("FFE8EDF5");
      cell.border = thinBorder;
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
    }
  });

  // ข้อมูล
  rows.forEach((record, index) => {
    const row = ws.addRow(columns.map((c) => c.value(record, index) ?? "-"));
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const col = columns[colNumber - 1];
      cell.border = thinBorder;
      const isNumber = typeof cell.value === "number";
      if (col.money && isNumber) cell.numFmt = MONEY_FMT;
      cell.alignment = {
        vertical: "top",
        horizontal: isNumber ? "right" : col.wrap ? "left" : undefined,
        wrapText: true,
      };
      if (index % 2 === 1) cell.fill = fill("FFFAFAFA");
    });
  });

  // แถวรวม (เฉพาะคอลัมน์เงิน)
  if (rows.length > 0) {
    const firstData = headRow + 1;
    const lastData = headRow + rows.length;
    const total = ws.addRow([]);
    total.getCell(1).value = "รวม";
    ws.mergeCells(total.number, 1, total.number, 3);
    columns.forEach((c, idx) => {
      if (!c.money) return;
      const letter = ws.getColumn(idx + 1).letter;
      total.getCell(idx + 1).value = {
        formula: `SUM(${letter}${firstData}:${letter}${lastData})`,
      };
      total.getCell(idx + 1).numFmt = MONEY_FMT;
    });
    total.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { bold: true };
      cell.fill = fill("FFFFF7E6");
      cell.border = thinBorder;
    });
  }
};

// ตารางสรุปแยกตามทนาย ต่อจากสรุปตามงาน (ใช้ตอนเลือกทนายทั้งหมด)
const addLawyerSummary = (ws, sheets, sum) => {
  const lawyerOf = (r) => r.LAWYER_NNAME || "ไม่ระบุทนาย";
  const lawyerNames = [
    ...new Set(sheets.flatMap((sheet) => sheet.rows.map(lawyerOf))),
  ].sort((a, b) => a.localeCompare(b, "th"));
  if (lawyerNames.length === 0) return;

  const countCols = sheets.length + 1; // จำนวนแต่ละงาน + รวมสัญญา
  const lastCol = 1 + countCols + 4;
  for (let c = 2; c <= lastCol; c += 1) {
    ws.getColumn(c).width = Math.max(ws.getColumn(c).width || 0, 16);
  }

  ws.addRow([]);
  const titleRow = ws.addRow(["สรุปตามทนาย"]);
  ws.mergeCells(titleRow.number, 1, titleRow.number, lastCol);
  titleRow.getCell(1).font = { bold: true, size: 14 };

  const header = ws.addRow([
    "ทนาย",
    ...sheets.map((sheet) => sheet.name),
    "รวมสัญญา",
    "ทุนทรัพย์ฟ้อง",
    "ค่าใช้จ่าย (เบิก)",
    "ค่าใช้จ่าย (จ่ายจริง)",
    "ยอดพิพากษา",
  ]);
  header.eachCell((cell, col) => {
    const sheet = sheets[col - 2];
    cell.font = { bold: true, color: sheet ? { argb: "FFFFFFFF" } : undefined };
    cell.fill = fill(sheet ? sheet.color : "FFE8EDF5");
    cell.border = thinBorder;
    cell.alignment = { horizontal: "center", wrapText: true };
  });

  const allRows = sheets.flatMap((sheet) => sheet.rows);
  const firstData = header.number + 1;
  lawyerNames.forEach((name) => {
    const rows = allRows.filter((r) => lawyerOf(r) === name);
    ws.addRow([
      name,
      ...sheets.map(
        (sheet) => sheet.rows.filter((r) => lawyerOf(r) === name).length,
      ),
      rows.length,
      sum(rows, (r) => r.litigation_funds),
      sum(rows, (r) => r.expenseWithdraw),
      sum(rows, (r) => r.expensePay),
      sum(rows, (r) => r.latestJudge?.judgement),
    ]);
  });
  const lastData = ws.lastRow.number;
  const total = ws.addRow(["รวม"]);
  for (let c = 2; c <= lastCol; c += 1) {
    const letter = ws.getColumn(c).letter;
    total.getCell(c).value = {
      formula: `SUM(${letter}${firstData}:${letter}${lastData})`,
    };
  }
  total.font = { bold: true };

  for (let r = firstData; r <= total.number; r += 1) {
    for (let c = 1; c <= lastCol; c += 1) {
      const cell = ws.getRow(r).getCell(c);
      cell.border = thinBorder;
      if (c > 1 + countCols) cell.numFmt = MONEY_FMT;
    }
  }
  total.eachCell((cell) => (cell.fill = fill("FFFFF7E6")));
};

const addSummarySheet = (workbook, sheets, subtitle, showLawyer) => {
  const ws = workbook.addWorksheet("สรุป");
  ws.columns = [
    { width: 24 },
    { width: 12 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
  ];
  ws.mergeCells("A1:F1");
  ws.getCell("A1").value = "สรุปตามงาน";
  ws.getCell("A1").font = { bold: true, size: 14 };
  ws.mergeCells("A2:F2");
  ws.getCell("A2").value = subtitle || "";
  ws.getCell("A2").font = { italic: true, color: { argb: "FF595959" } };

  const header = ws.addRow([
    "งาน",
    "จำนวนสัญญา",
    "ทุนทรัพย์ฟ้อง",
    "ค่าใช้จ่าย (เบิก)",
    "ค่าใช้จ่าย (จ่ายจริง)",
    "ยอดพิพากษา",
  ]);
  header.eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = fill("FFE8EDF5");
    cell.border = thinBorder;
    cell.alignment = { horizontal: "center" };
  });

  const sum = (rows, fn) =>
    rows.reduce((s, r) => s + (toNumber(fn(r)) || 0), 0);
  const firstData = header.number + 1;
  sheets.forEach((sheet) => {
    const row = ws.addRow([
      sheet.name,
      sheet.rows.length,
      sum(sheet.rows, (r) => r.litigation_funds),
      sum(sheet.rows, (r) => r.expenseWithdraw),
      sum(sheet.rows, (r) => r.expensePay),
      sum(sheet.rows, (r) => r.latestJudge?.judgement),
    ]);
    row.getCell(1).fill = fill(sheet.color);
    row.getCell(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  });
  const lastData = ws.lastRow.number;
  const total = ws.addRow(["รวม"]);
  ["B", "C", "D", "E", "F"].forEach((letter) => {
    total.getCell(letter).value = {
      formula: `SUM(${letter}${firstData}:${letter}${lastData})`,
    };
  });
  total.font = { bold: true };

  for (let r = firstData; r <= total.number; r += 1) {
    ws.getRow(r).eachCell({ includeEmpty: true }, (cell, col) => {
      cell.border = thinBorder;
      if (col >= 3) cell.numFmt = MONEY_FMT;
    });
  }
  total.eachCell((cell) => (cell.fill = fill("FFFFF7E6")));

  if (showLawyer) addLawyerSummary(ws, sheets, sum);
};

/**
 * export excel ตามตาราง แยก sheet ตามงาน
 * sheets: [{ name, color, rows: enrichCase[] }]
 */
export const exportCasesExcel = async ({
  sheets,
  fileName,
  subtitle,
  formatDate,
  withSummary = true,
  showLawyer = false,
}) => {
  const workbook = new ExcelJS.Workbook();
  const columns = buildColumns(formatDate, showLawyer);
  // รับได้ทั้ง name หรือ label (STATUS_GROUPS)
  sheets = sheets.map((sheet) => ({
    ...sheet,
    name: sheet.name || sheet.label,
  }));

  if (withSummary && sheets.length > 1) {
    addSummarySheet(workbook, sheets, subtitle, showLawyer);
  }
  sheets
    .filter((sheet) => sheet.rows.length > 0 || sheets.length === 1)
    .forEach((sheet) =>
      addCaseSheet(workbook, { ...sheet, subtitle }, columns),
    );

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    fileName,
  );
};
